"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Hammer, ShieldCheck, Flame, Trees, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const features = [{
  icon: Hammer,
  title: "Timber framing",
  desc: "Heavy timber & custom cedar joinery"
}, {
  icon: Flame,
  title: "Stone masonry",
  desc: "Custom hearths & integrated fire pits"
}, {
  icon: ShieldCheck,
  title: "All-weather builds",
  desc: "Weather-sealed electric & plumbing"
}, {
  icon: Trees,
  title: "Native integration",
  desc: "Harmonious tree & lot preservation"
}];
export default function Outdoor() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve = [0.16, 1, 0.3, 1] as const;
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: easeCurve
      }
    }
  };
  return <section id="outdoor-living" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Editorial Column (5 cols on desktop) */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="flex flex-col lg:col-span-5">
            <div className="mb-4">
              <Badge variant="secondary" className="rounded-sm px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                Decatur outdoor architecture
              </Badge>
            </div>

            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl lg:leading-[1.15]">
              Extend your home into the open air.
            </h2>

            {/* Architectural Copper Hairline Accent */}
            <div className="mt-4 mb-6 h-0.5 w-16 bg-primary" />

            <p className="font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              Screened porches, flagstone terraces, and fully integrated outdoor kitchens designed to withstand Georgia summers and provide year-round comfort. We craft outdoor rooms with the same structural integrity and fine finishes found inside your home.
            </p>

            {/* 2-Column Feature Highlight List */}
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return <div key={feature.title} data-index={index} className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 p-3.5 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-xs">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-sans text-sm font-semibold text-foreground">
                        {feature.title}
                      </h4>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {feature.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Actions */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg font-medium shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Schedule a consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-lg font-medium">
                <Link to="/ProjectsPage">
                  View porch portfolio
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Visual Column (7 cols on desktop) */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="relative lg:col-span-7">
            <div className="relative overflow-hidden rounded-xl border border-border shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_outdoor_living_patio.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_outdoor_living_patio.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_outdoor_living_patio.png-wvc-sizes" alt="Decatur custom screened porch and stone patio with outdoor hearth" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover sm:aspect-[16/11]" />

              {/* Decorative Subtle Gradient Overlay */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent" />
            </div>

            {/* Layered Translucent Frosted Glass Card */}
            <div className="relative mt-4 sm:absolute sm:-bottom-6 sm:left-6 sm:right-6 sm:mt-0 sm:max-w-md rounded-xl border border-border bg-card/85 p-5 shadow-lg backdrop-blur-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40">
              <div className="flex items-start gap-3.5">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                  <CheckCircle2 className="size-5" />
                </div>
                <div>
                  <h3 className="font-sans text-sm font-semibold text-card-foreground">
                    Decatur Zoning & Tree Protection
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Complete management of city permits, impervious surface allowances, and historic neighborhood guidelines from start to finish.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}